import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, BookOpen, Video, Package, Briefcase, Sparkles, Users, Check, Star, FileText, Calendar, Dumbbell, Link, NotebookPen, Mic, Mail, Columns3, Grid3X3, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import monetizationLogo from "@/assets/monetization-logo-new.png";
import { useCredits } from "@/hooks/useCredits";
import { MonetizationStepper } from "@/components/monetization/MonetizationStepper";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

interface ProductIdea {
  rank: number;
  name: string;
  type: string;
  typeLabel: string;
  description: string;
  fitScore: number;
  whyItFits: string;
  difficulty: string;
  timeToLaunch: string;
  monetizationPotential: string;
}

interface Analysis {
  niche: string;
  audience: string;
  strengths: string[];
  opportunities: string[];
}

const PRODUCT_ICONS: Record<string, React.ReactNode> = {
  ebook: <BookOpen className="w-6 h-6" />,
  video_course: <Video className="w-6 h-6" />,
  asset_pack: <Package className="w-6 h-6" />,
  toolkit: <Check className="w-6 h-6" />,
  mini_course: <Sparkles className="w-6 h-6" />,
  coaching: <Users className="w-6 h-6" />,
  notion_template: <FileText className="w-6 h-6" />,
  swipe_file: <FileText className="w-6 h-6" />,
  newsletter_pack: <Mail className="w-6 h-6" />,
  challenge_kit: <Dumbbell className="w-6 h-6" />,
  resource_bundle: <Link className="w-6 h-6" />,
  content_calendar: <Calendar className="w-6 h-6" />,
  workbook: <NotebookPen className="w-6 h-6" />,
  coaching_scripts: <Mic className="w-6 h-6" />,
};

const TYPE_LABELS: Record<string, string> = {
  ebook: "📘 eBook",
  video_course: "🎥 Video Course",
  asset_pack: "🗂 Asset Pack",
  toolkit: "✅ Templates & Checklists",
  mini_course: "🧠 Mini Course",
  coaching: "🧑‍🏫 Coaching",
  notion_template: "📋 Notion Template Pack",
  swipe_file: "📝 Swipe File",
  newsletter_pack: "📰 Newsletter Pack",
  challenge_kit: "🏋 Challenge Kit",
  resource_bundle: "🔗 Resource Bundle",
  content_calendar: "📅 Content Calendar",
  workbook: "📓 Workbook",
  coaching_scripts: "🎙 Coaching Scripts",
};

// Parse price string like "$27-$97" to get midpoint
const parsePriceMidpoint = (potential: string): number | null => {
  const match = potential.match(/\$(\d+)(?:\s*[-–]\s*\$(\d+))?/);
  if (!match) return null;
  const low = parseInt(match[1]);
  const high = match[2] ? parseInt(match[2]) : low;
  return Math.round((low + high) / 2);
};

export default function MonetizationProducts() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { consume } = useCredits();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [project, setProject] = useState<any>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [products, setProducts] = useState<ProductIdea[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductIdea | null>(null);
  const [favourites, setFavourites] = useState<Set<string>>(new Set());
  const [showComparison, setShowComparison] = useState(false);
  const [expandedWhy, setExpandedWhy] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) fetchProjectAndProducts();
  }, [projectId]);

  const fetchProjectAndProducts = async () => {
    try {
      const { data: projectData, error: projectError } = await supabase
        .from("monetization_projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (projectError) throw projectError;
      setProject(projectData);

      const { data: existingProducts } = await supabase
        .from("monetization_products")
        .select("*")
        .eq("project_id", projectId)
        .eq("status", "idea");

      if (existingProducts && existingProducts.length > 0) {
        const loadedProducts = existingProducts.map((p) => ({
          rank: p.fit_score,
          name: p.title,
          type: p.product_type,
          typeLabel: TYPE_LABELS[p.product_type] || p.product_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
          description: (p.content as any)?.description || "",
          fitScore: p.fit_score,
          whyItFits: p.why_it_fits || "",
          difficulty: p.difficulty || "medium",
          timeToLaunch: p.time_to_launch || "",
          monetizationPotential: p.monetization_potential || "",
        }));
        setProducts(loadedProducts);
        setLoading(false);
      } else {
        await generateProducts(projectData);
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
      setLoading(false);
    }
  };

  const generateProducts = async (projectData: any) => {
    setGenerating(true);
    setLoading(true);
    try {
      const credited = await consume("monetization-generate-products", "Generate product ideas");
      if (!credited) { setGenerating(false); setLoading(false); return; }

      const maxRetries = 2;
      let lastError: any = null;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          if (attempt > 0) {
            const delay = Math.pow(2, attempt - 1) * 1000;
            await new Promise(r => setTimeout(r, delay));
          }

          const { data, error } = await supabase.functions.invoke("monetization-ai", {
            body: {
              type: "generate-product-ideas",
              params: {
                entryType: projectData.entry_type,
                script: projectData.script_input,
                niche: projectData.niche,
                audienceType: projectData.audience_type,
                platform: projectData.platform,
                skillLevel: projectData.skill_level,
                goal: projectData.goal,
                idea: projectData.idea_input,
                painPoints: projectData.audience_pain_points,
              },
            },
          });

          if (error) throw error;
          if (data?.error) throw new Error(data.error);

          const productsArray = data?.products || [];
          if (productsArray.length === 0) throw new Error("AI returned no product ideas. Please try again.");

          setAnalysis(data.analysis);
          setProducts(productsArray);

          for (const product of productsArray) {
            await supabase.from("monetization_products").insert({
              project_id: projectId,
              product_type: product.type,
              title: product.name,
              fit_score: product.fitScore,
              why_it_fits: product.whyItFits,
              difficulty: product.difficulty,
              time_to_launch: product.timeToLaunch,
              monetization_potential: product.monetizationPotential,
              content: { description: product.description },
              status: "idea",
            });
          }

          lastError = null;
          break;
        } catch (err) {
          lastError = err;
          console.warn(`Product generation attempt ${attempt + 1} failed:`, err);
        }
      }

      if (lastError) throw lastError;
    } catch (error: any) {
      console.error("Error generating products:", error);
      toast.error(error.message || "Generation failed. Please try again.");
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  };

  const handleSelectProduct = (product: ProductIdea) => {
    setSelectedProduct(product);
  };

  const toggleFavourite = (e: React.MouseEvent, productName: string) => {
    e.stopPropagation();
    setFavourites(prev => {
      const next = new Set(prev);
      if (next.has(productName)) next.delete(productName);
      else next.add(productName);
      return next;
    });
  };

  const handleBuildProduct = async () => {
    if (!selectedProduct) return;

    await supabase
      .from("monetization_projects")
      .update({ last_step: "building" })
      .eq("id", projectId);

    const { data: productData } = await supabase
      .from("monetization_products")
      .select("id")
      .eq("project_id", projectId)
      .eq("title", selectedProduct.name)
      .single();

    if (productData) {
      if (selectedProduct.type === "asset_pack") {
        navigate(`/app/monetization-ai/project/${projectId}/assets/${productData.id}`);
      } else {
        navigate(`/app/monetization-ai/project/${projectId}/format/${productData.id}`);
      }
    }
  };

  // Determine recommended pick (highest fit score)
  const recommendedProduct = products.length > 0
    ? products.reduce((best, p) => p.fitScore > best.fitScore ? p : best, products[0])
    : null;

  // Top 3 for comparison view
  const top3 = [...products].sort((a, b) => b.fitScore - a.fitScore).slice(0, 3);

  if (loading || generating) {
    return (
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl overflow-hidden">
              <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold">Product Ideas</h1>
              <p className="text-muted-foreground">{generating ? "Generating product ideas..." : "Loading..."}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="glass-card p-5 animate-pulse">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl bg-muted" />
                  <div className="w-16 h-6 rounded bg-muted" />
                </div>
                <div className="space-y-2 mb-3">
                  <div className="h-4 w-20 rounded bg-muted" />
                  <div className="h-5 w-3/4 rounded bg-muted" />
                  <div className="h-4 w-full rounded bg-muted" />
                </div>
                <div className="h-12 rounded-lg bg-muted/50 mb-3" />
                <div className="flex justify-between">
                  <div className="h-5 w-16 rounded-full bg-muted" />
                  <div className="h-5 w-12 rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!loading && !generating && products.length === 0) {
    return (
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto text-center py-16">
          <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-display text-xl font-semibold mb-2">Generation failed</h2>
          <p className="text-muted-foreground mb-6">
            We couldn't generate product ideas this time. Please try again.
          </p>
          <Button variant="hero" onClick={() => generateProducts(project)} disabled={generating}>
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Retry Generation
          </Button>
        </div>
      </div>
    );
  }

  const renderProductCard = (product: ProductIdea, i: number, isCompact = false) => {
    const isSelected = selectedProduct?.name === product.name;
    const isFavourited = favourites.has(product.name);
    const isRecommended = recommendedProduct?.name === product.name;
    const price = parsePriceMidpoint(product.monetizationPotential);

    return (
      <div
        key={i}
        onClick={() => handleSelectProduct(product)}
        className={`glass-card p-5 cursor-pointer transition-all relative ${
          isSelected
            ? "border-green-500 bg-green-500/10 ring-2 ring-green-500/20"
            : isRecommended
            ? "border-primary/50 ring-1 ring-primary/20"
            : "border-border/50 hover:border-green-500/50"
        }`}
      >
        {/* Recommended badge */}
        {isRecommended && !isCompact && (
          <div className="absolute -top-3 left-4 px-2.5 py-0.5 bg-primary text-primary-foreground text-[10px] font-semibold rounded-full flex items-center gap-1">
            <Trophy className="w-3 h-3" />
            Best fit for your audience
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isSelected ? "bg-green-500/20" : "bg-muted"
          }`}>
            {PRODUCT_ICONS[product.type] || <Package className="w-6 h-6" />}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => toggleFavourite(e, product.name)}
              className="p-1 rounded-md hover:bg-muted transition-colors"
            >
              <Star className={`w-4 h-4 ${isFavourited ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground"}`} />
            </button>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="font-bold text-lg">{product.fitScore}</span>
              <span className="text-xs text-muted-foreground">/10</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mb-3">
          <span className="text-xs text-green-400 mb-1 block">{product.typeLabel}</span>
          <h3 className="font-display font-semibold text-foreground mb-1">{product.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
        </div>

        {/* Why it fits - expandable */}
        <Collapsible open={expandedWhy === product.name} onOpenChange={(open) => setExpandedWhy(open ? product.name : null)}>
          <CollapsibleTrigger
            onClick={(e) => e.stopPropagation()}
            className="w-full p-2 rounded-lg bg-muted/50 mb-3 flex items-center justify-between text-left hover:bg-muted/70 transition-colors"
          >
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Why this one?</span>
            </p>
            <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${expandedWhy === product.name ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-2 rounded-lg bg-muted/30 mb-3">
              <p className="text-xs text-muted-foreground">{product.whyItFits}</p>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Revenue projection */}
        {price && (
          <div className="p-2 rounded-lg bg-green-500/5 border border-green-500/10 mb-3">
            <p className="text-xs text-green-400">
              💰 At 1% conversion of 1,000 followers: <span className="font-semibold">~${Math.round(price * 10)}/month</span>
            </p>
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full ${
              product.difficulty === "easy" ? "bg-green-500/20 text-green-400" :
              product.difficulty === "medium" ? "bg-yellow-500/20 text-yellow-400" :
              "bg-red-500/20 text-red-400"
            }`}>
              {product.difficulty}
            </span>
            <span className="text-muted-foreground">{product.timeToLaunch}</span>
          </div>
          <span className="font-medium text-green-400">{product.monetizationPotential}</span>
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/app/monetization-ai/project/${projectId}/input`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl overflow-hidden">
            <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Product Ideas</h1>
            <p className="text-muted-foreground">Select a product to build</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="ml-14">
          <MonetizationStepper currentStep="products" />
        </div>

        {/* Analysis Summary */}
        {analysis && (
          <div className="glass-card p-6 border-green-500/20 mb-8">
            <h3 className="font-display text-lg font-semibold mb-4">AI Analysis</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Niche</p>
                <p className="font-medium">{analysis.niche}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Target Audience</p>
                <p className="font-medium">{analysis.audience}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Strengths</p>
                <div className="flex flex-wrap gap-2">
                  {analysis.strengths?.map((s, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">{s}</span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Opportunities</p>
                <div className="flex flex-wrap gap-2">
                  {analysis.opportunities?.map((o, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary">{o}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View toggle + favourites filter */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button
              variant={showComparison ? "default" : "outline"}
              size="sm"
              onClick={() => setShowComparison(!showComparison)}
            >
              <Columns3 className="w-4 h-4 mr-1.5" />
              Compare Top 3
            </Button>
            {favourites.size > 0 && (
              <span className="text-xs text-muted-foreground">
                <Star className="w-3 h-3 inline text-yellow-500 fill-yellow-500" /> {favourites.size} favourited
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">{products.length} ideas generated</span>
        </div>

        {/* Comparison View */}
        {showComparison && (
          <div className="mb-8">
            <h3 className="font-display text-lg font-semibold mb-4">Top 3 Comparison</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {top3.map((product, i) => renderProductCard(product, i, true))}
            </div>
          </div>
        )}

        {/* Products Grid */}
        {!showComparison && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {/* Show favourited first */}
            {[...products]
              .sort((a, b) => {
                const aFav = favourites.has(a.name) ? 1 : 0;
                const bFav = favourites.has(b.name) ? 1 : 0;
                return bFav - aFav;
              })
              .map((product, i) => renderProductCard(product, i))}
          </div>
        )}

        {/* Continue Button */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => generateProducts(project)}
            disabled={generating}
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Regenerate Ideas
          </Button>
          <Button
            variant="hero"
            size="lg"
            onClick={handleBuildProduct}
            disabled={!selectedProduct}
          >
            Build This Product
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
